import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/supabaseClient";

const isMissingSubscriptionTagsTable = (err: any) =>
  err?.code === "PGRST205" ||
  (typeof err?.message === "string" &&
    err.message.includes(
      "Could not find the table 'public.subscription_tags'",
    ));

export interface DatabaseSubscriptionTag {
  id: string;
  user_id: string;
  subscription_id: string;
  category_id: string;
  created_at: string;
}

export const useSubscriptionTags = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ["subscription_tags", user?.id];

  const {
    data: subscriptionTags = [],
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!user) return [];
      try {
        const { data, error } = await supabase
          .from("subscription_tags")
          .select("*")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false });

        if (error) throw error;
        return data as DatabaseSubscriptionTag[];
      } catch (e: any) {
        if (isMissingSubscriptionTagsTable(e)) {
          return [];
        }
        throw e;
      }
    },
    enabled: !!user,
  });

  const tagsBySubscriptionId: Record<string, string[]> =
    subscriptionTags.reduce(
      (acc, row) => {
        (acc[row.subscription_id] ||= []).push(row.category_id);
        return acc;
      },
      {} as Record<string, string[]>,
    );

  const addTag = useMutation({
    mutationKey: ["addSubscriptionTag"],
    mutationFn: async (params: {
      subscriptionId: string;
      categoryId: string;
    }) => {
      if (!user) throw new Error("User not authenticated");
      try {
        const { data, error } = await supabase
          .from("subscription_tags")
          .insert({
            user_id: user.id,
            subscription_id: params.subscriptionId,
            category_id: params.categoryId,
          })
          .select()
          .single();

        if (error) throw error;
        return data as DatabaseSubscriptionTag;
      } catch (e: any) {
        if (isMissingSubscriptionTagsTable(e)) {
          return {
            id: "noop",
            user_id: user.id,
            subscription_id: params.subscriptionId,
            category_id: params.categoryId,
            created_at: new Date().toISOString(),
          } as DatabaseSubscriptionTag;
        }
        throw e;
      }
    },
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<DatabaseSubscriptionTag[]>(queryKey);
      queryClient.setQueryData<DatabaseSubscriptionTag[]>(
        queryKey,
        (old = []) => [
          {
            id: crypto.randomUUID(),
            user_id: user?.id ?? "",
            subscription_id: params.subscriptionId,
            category_id: params.categoryId,
            created_at: new Date().toISOString(),
          },
          ...old,
        ],
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const removeTag = useMutation({
    mutationKey: ["removeSubscriptionTag"],
    mutationFn: async (params: {
      subscriptionId: string;
      categoryId: string;
    }) => {
      if (!user) throw new Error("User not authenticated");
      try {
        const { error } = await supabase
          .from("subscription_tags")
          .delete()
          .eq("subscription_id", params.subscriptionId)
          .eq("category_id", params.categoryId);

        if (error) throw error;
      } catch (e: any) {
        if (isMissingSubscriptionTagsTable(e)) {
          return;
        }
        throw e;
      }
    },
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<DatabaseSubscriptionTag[]>(queryKey);
      queryClient.setQueryData<DatabaseSubscriptionTag[]>(
        queryKey,
        (old = []) =>
          old.filter(
            (tag) =>
              !(
                tag.subscription_id === params.subscriptionId &&
                tag.category_id === params.categoryId
              ),
          ),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const replaceTags = useMutation({
    mutationFn: async (params: {
      subscriptionId: string;
      categoryIds: string[];
    }) => {
      if (!user) throw new Error("User not authenticated");
      try {
        const currentIds =
          tagsBySubscriptionId[params.subscriptionId]?.slice() ??
          (
            await supabase
              .from("subscription_tags")
              .select("category_id")
              .eq("subscription_id", params.subscriptionId)
          ).data?.map((r: any) => r.category_id) ??
          [];

        const desired = new Set(params.categoryIds);
        const current = new Set(currentIds);

        const toAdd = [...desired].filter((id) => !current.has(id));
        const toRemove = [...current].filter((id) => !desired.has(id));

        if (toAdd.length > 0) {
          const { error: insertErr } = await supabase
            .from("subscription_tags")
            .insert(
              toAdd.map((categoryId) => ({
                user_id: user.id,
                subscription_id: params.subscriptionId,
                category_id: categoryId,
              })),
            );
          if (insertErr) throw insertErr;
        }

        if (toRemove.length > 0) {
          const { error: deleteErr } = await supabase
            .from("subscription_tags")
            .delete()
            .eq("subscription_id", params.subscriptionId)
            .in("category_id", toRemove);
          if (deleteErr) throw deleteErr;
        }

        return { added: toAdd, removed: toRemove };
      } catch (e: any) {
        if (isMissingSubscriptionTagsTable(e)) {
          return { added: [], removed: [] };
        }
        throw e;
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    subscriptionTags,
    tagsBySubscriptionId,
    isLoading,
    error,
    addTag,
    removeTag,
    replaceTags,
  };
};
